import React from 'react';
import { Info, AlertTriangle, CheckCircle } from 'lucide-react';

/**
 * CompactUsageIndicator Component
 * A compact version of usage stats for displaying near input areas
 * 
 * @param {Object} props
 * @param {Object} props.stats - Usage statistics object
 * @param {number} props.currentVersions - Current version count
 * @param {string} props.type - Type: 'edit' or 'create'
 */
export default function CompactUsageIndicator({ stats, currentVersions = 0, type = 'edit' }) {
  if (!stats) return null;

  if (type === 'edit') {
    const { allowed } = stats.editsPerCollateral;
    const remaining = Math.max(0, allowed - currentVersions);
    const percentage = (currentVersions / allowed) * 100;
    
    // Determine status
    const isAtLimit = remaining === 0;
    const isNearLimit = percentage >= 70 && !isAtLimit;
    
    // Status color
    const getStatusColor = () => {
      if (isAtLimit) return '#FF6B5A';
      if (isNearLimit) return '#FFA500';
      return '#10B981';
    };
    
    const statusColor = getStatusColor();
    
    // Status icon
    const StatusIcon = isAtLimit ? AlertTriangle : isNearLimit ? Info : CheckCircle;

    return (
      <div 
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs border"
        style={{ 
          backgroundColor: `${statusColor}10`,
          borderColor: `${statusColor}40`,
          color: statusColor
        }}
      >
        <StatusIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: statusColor }} />
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="font-medium whitespace-nowrap">
            v{currentVersions}/{allowed}
          </span>
          <span className="opacity-75">•</span>
          <span className="truncate">
            {remaining} edit{remaining !== 1 ? 's' : ''} left
          </span>
        </div>
        {/* Mini progress indicator */}
        <div 
          className="w-12 h-1.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: `${statusColor}20` }}
        >
          <div 
            className="h-full rounded-full transition-all duration-300"
            style={{ 
              width: `${percentage}%`,
              backgroundColor: statusColor
            }}
          />
        </div>
      </div>
    );
  }

  if (type === 'create') {
    const { allowed, created, remaining, percentage } = stats.collaterals;
    const isAtLimit = remaining === 0;
    const isNearLimit = percentage >= 70 && !isAtLimit;
    
    const getStatusColor = () => {
      if (isAtLimit) return '#FF6B5A';
      if (isNearLimit) return '#FFA500';
      return '#10B981';
    };
    
    const statusColor = getStatusColor();
    const StatusIcon = isAtLimit ? AlertTriangle : isNearLimit ? Info : CheckCircle;

    return (
      <div 
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs border"
        style={{ 
          backgroundColor: `${statusColor}10`,
          borderColor: `${statusColor}40`,
          color: statusColor
        }}
      >
        <StatusIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: statusColor }} />
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="font-medium whitespace-nowrap">
            {created}/{allowed}
          </span>
          <span className="opacity-75">•</span>
          <span className="truncate">
            {remaining} left
          </span>
        </div>
        <div 
          className="w-12 h-1.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: `${statusColor}20` }}
        >
          <div 
            className="h-full rounded-full transition-all duration-300"
            style={{ 
              width: `${percentage}%`,
              backgroundColor: statusColor
            }}
          />
        </div>
      </div>
    );
  }

  return null;
}

